'use client'

import React from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { User, Calendar, MoreHorizontal, Trash2 } from 'lucide-react'
import { useTranslations } from '@/lib/i18n'
import Link from 'next/link'
import { getTrackerBadgeStyle, getStatusBadgeStyle, getPriorityBadgeStyle } from '@/lib/badge-utils'
import { IssueTimeTracker } from './issue-time-tracker'

interface Issue {
  id: number
  number?: number
  subject: string
  description?: string
  tracker?: {
    id: number
    name: string
  }
  status?: {
    id: number
    name: string
    is_closed: boolean
  }
  priority?: {
    id: number
    name: string
  }
  author?: {
    id: number
    name: string
  }
  assigned_to?: {
    id: number
    name: string
  }
  category?: {
    id: number
    name: string
  }
  fixed_version?: {
    id: number
    name: string
  }
  parent?: {
    id: number
    subject: string
  }
  children?: Issue[]
  start_date?: string
  due_date?: string
  done_ratio?: number
  estimated_hours?: number
  spent_hours?: number
  created_on?: string
  updated_on?: string
  custom_fields?: Array<{
    id: number
    name: string
    value: string
  }>
  project?: {
    id: number
    name: string
  }
}

interface IssueCardProps {
  issue: Issue
  onOpenMenu?: (issue: Issue) => void
  onDelete?: (issue: Issue) => void
  onOpenTimeTracking?: () => void
  onClickIssue?: (issue: Issue) => void
  showTimeTracker?: boolean
  compact?: boolean
  showProjectName?: boolean
  customActions?: React.ReactNode
}

const getPriorityIcon = (priority: string) => {
  const icons: Record<string, JSX.Element> = {
    'Low': <span className="text-green-600">↓</span>,
    'Normal': <span className="text-blue-600">−</span>,
    'High': <span className="text-orange-600">↑</span>,
    'Urgent': <span className="text-red-600">↑</span>,
    'Immediate': <span className="text-purple-600">↑</span>
  }
  return icons[priority] || <span className="text-gray-600">−</span>
}

// Memoize IssueCard to prevent unnecessary re-renders
// Only re-render when issue data, callbacks, or flags actually change
const IssueCardComponent = ({
  issue,
  onOpenMenu,
  onDelete,
  onOpenTimeTracking,
  onClickIssue,
  showTimeTracker = true,
  compact = false,
  showProjectName,
  customActions
}: IssueCardProps) => {

  const t = useTranslations('issuesPage')

  const handleIssueClick = (e: React.MouseEvent) => {
    if (onClickIssue) {
      e.preventDefault()
      onClickIssue(issue)
    }
  }

  return (
    <Card className="hover:shadow-sm transition-shadow border border-border">
      <CardContent className={compact ? "p-4" : "p-5"}>
        {/* Full width content */}
        <div className="space-y-3">
          {/* Header Row - Pills and Actions */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
              <Link
                href={`/issues/${issue.id}`}
                onClick={handleIssueClick}
                className="font-semibold text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                #{issue.number ?? issue.id}
              </Link>
              <span className="text-muted-foreground">·</span>
              {issue.tracker && (
                <Badge
                  variant={getTrackerBadgeStyle(issue.tracker.name).variant}
                  className={`text-xs font-normal ${getTrackerBadgeStyle(issue.tracker.name).className}`}
                >
                  {issue.tracker.name}
                </Badge>
              )}
              {issue.status && (
                <Badge
                  variant={getStatusBadgeStyle(issue.status.name, issue.status.is_closed).variant}
                  className={`text-xs font-normal whitespace-nowrap ${getStatusBadgeStyle(issue.status.name, issue.status.is_closed).className}`}
                >
                  {issue.status.name}
                </Badge>
              )}
              {issue.priority && (
                <Badge
                  variant={getPriorityBadgeStyle(issue.priority.id, issue.priority.name).variant}
                  className={`text-xs font-normal ${getPriorityBadgeStyle(issue.priority.id, issue.priority.name).className}`}
                >
                  <div className="flex items-center gap-1">
                    {getPriorityIcon(issue.priority.name)}
                    <span>{issue.priority.name}</span>
                  </div>
                </Badge>
              )}
            </div>
            
            {/* Actions in header */}
            <div className="shrink-0 flex items-center gap-1">
              {customActions}
              {onDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(issue) }}
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                  title={t('deleteIssue')}
                  aria-label={t('deleteIssue')}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              {onOpenMenu && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onOpenMenu(issue)}
                  className="h-8 w-8 p-0"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

            {/* Title */}
            <Link
              href={`/issues/${issue.id}`}
              onClick={handleIssueClick}
              className="block"
            >
              <h3 className={`font-medium ${compact ? 'text-sm' : 'text-base'} text-foreground hover:text-primary transition-colors line-clamp-1`}>
                {issue.subject}
              </h3>
              {/* Project name under the title */}
                { (typeof showProjectName === 'undefined' || showProjectName) && (
                  <div className="text-xs text-muted-foreground mt-1 truncate">{issue.project?.name}</div>
                ) }
            </Link>

            {/* Description */}
            {!compact && issue.description && (
              <div>
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {issue.description}
                </p>
              </div>
            )}

            {/* Full width divider */}
            {!compact && <div className="border-t border-border -mx-5" />}
            
            {/* Meta information */}
            <div className={`${!compact && 'pt-3'}`}>
              <div className="flex items-center justify-between gap-6 text-xs text-muted-foreground">
                <div className="flex items-center gap-6">
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <User className="h-3.5 w-3.5" />
                    {issue.assigned_to?.name || t('unassigned')}
                  </span>
                  {issue.updated_on && (
                    <span className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" />
                      {new Date(issue.updated_on).toLocaleDateString()}
                    </span>
                  )}
                  {/* Time Tracking */}
                  {showTimeTracker && (
                    <IssueTimeTracker
                      issueId={issue.id}
                      issueSubject={issue.subject}
                      totalHours={issue.spent_hours}
                      variant="compact"
                      onOpenTimeTracking={onOpenTimeTracking}
                    />
                  )}
                </div>
                {(issue.done_ratio || 0) > 0 && (
                  <span className="flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-foreground rounded-full transition-all"
                        style={{ width: `${issue.done_ratio}%` }}
                      />
                    </div>
                    <span>{issue.done_ratio}%</span>
                  </span>
                )}
              </div>
            </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Custom comparison function for React.memo
// Compare issue properties that matter for rendering
const arePropsEqual = (prevProps: IssueCardProps, nextProps: IssueCardProps) => {
  // Quick reference check - if issue object is the same, skip deep comparison
  if (prevProps.issue === nextProps.issue) {
    return (
      prevProps.showTimeTracker === nextProps.showTimeTracker &&
      prevProps.compact === nextProps.compact &&
      prevProps.showProjectName === nextProps.showProjectName &&
      prevProps.onOpenMenu === nextProps.onOpenMenu &&
      prevProps.onDelete === nextProps.onDelete &&
      prevProps.onOpenTimeTracking === nextProps.onOpenTimeTracking &&
      prevProps.onClickIssue === nextProps.onClickIssue
    )
  }

  // Deep comparison of issue properties that affect rendering
  const prevIssue = prevProps.issue
  const nextIssue = nextProps.issue

  return (
    prevIssue.id === nextIssue.id &&
    prevIssue.subject === nextIssue.subject &&
    prevIssue.done_ratio === nextIssue.done_ratio &&
    prevIssue.updated_on === nextIssue.updated_on &&
    prevIssue.spent_hours === nextIssue.spent_hours &&
    prevIssue.tracker?.id === nextIssue.tracker?.id &&
    prevIssue.tracker?.name === nextIssue.tracker?.name &&
    prevIssue.status?.id === nextIssue.status?.id &&
    prevIssue.status?.name === nextIssue.status?.name &&
    prevIssue.status?.is_closed === nextIssue.status?.is_closed &&
    prevIssue.priority?.id === nextIssue.priority?.id &&
    prevIssue.priority?.name === nextIssue.priority?.name &&
    prevIssue.assigned_to?.id === nextIssue.assigned_to?.id &&
    prevIssue.assigned_to?.name === nextIssue.assigned_to?.name &&
    prevIssue.project?.id === nextIssue.project?.id &&
    prevIssue.project?.name === nextIssue.project?.name &&
    prevIssue.parent?.id === nextIssue.parent?.id &&
    prevIssue.fixed_version?.id === nextIssue.fixed_version?.id &&
    prevIssue.category?.id === nextIssue.category?.id &&
    prevProps.showTimeTracker === nextProps.showTimeTracker &&
    prevProps.compact === nextProps.compact &&
    prevProps.showProjectName === nextProps.showProjectName &&
    prevProps.onOpenMenu === nextProps.onOpenMenu &&
    prevProps.onOpenTimeTracking === nextProps.onOpenTimeTracking &&
    prevProps.onClickIssue === nextProps.onClickIssue
  )
}

// Export memoized component
export const IssueCard = React.memo(IssueCardComponent, arePropsEqual)
